<template>
  <div :style="contextBarStyle">
    <div ref="contextBarRef" class="inline-flex items-center gap-1 py-1 px-1 rounded-md shadow-md bg-white dark:bg-charcoal-800 dark:ring-1 dark:ring-charcoal-600">
      <core-tooltip text="Delete" placement="top">
        <u-button color="charcoal" size="sm" variant="ghost" :icon="COMMON_ICONS.delete" class="dark:text-white" @click="deleteElement" @pointerdown.stop />
      </core-tooltip>
      <core-tooltip text="Duplicate" placement="top">
        <u-button color="charcoal" size="sm" variant="ghost" icon="i-ri-file-copy-line" class="dark:text-white" @click="duplicateElement" @pointerdown.stop />
      </core-tooltip>
      <surface-editor-core-toolbar-divider />
      <u-dropdown :items="overflowItems">
        <core-tooltip text="More actions" placement="top">
          <u-button color="charcoal" size="sm" variant="ghost" icon="i-ri-more-2-fill" class="dark:text-white" @pointerdown.stop />
        </core-tooltip>
      </u-dropdown>
    </div>
  </div>
</template>

<script setup>

  import {storeToRefs} from 'pinia';

  const props = defineProps({
    element: Object
  });

  const surfaceEditorStore = useSurfaceEditorStore();
  const {scale, clipboard, surfaces} = storeToRefs(surfaceEditorStore);
  const surfaceEditorUtils = useSurfaceEditorUtils();

  const contextBarRef = ref(null);
  const {width: contextBarWidth} = useElementSize(contextBarRef);

  const contextBarHeight = 42;
  const contextBarOffset = 8;
  const contextBarPlacement = computed(() => {
    const rotation = props.element.rotation || 0;
    let extraAbove = 0;
    let extraBelow = 0;

    if (rotation) {
      // Calculate the highest point of the rotated element across the entire width
      // of the context bar to avoid overlap with corners
      const w = props.element.width;
      const h = props.element.height;
      const radians = (rotation * Math.PI) / 180;
      const cos = Math.cos(radians);
      const sin = Math.sin(radians);

      // Calculate the four corners of the rotated rectangle in local coordinates
      // (relative to element center)
      const corners = [
        {x: -w / 2, y: -h / 2}, // top-left
        {x: w / 2, y: -h / 2},  // top-right
        {x: w / 2, y: h / 2},   // bottom-right
        {x: -w / 2, y: h / 2}   // bottom-left
      ];

      // Rotate corners
      const rotatedCorners = corners.map(c => ({
        x: c.x * cos - c.y * sin,
        y: c.x * sin + c.y * cos
      }));

      // Check multiple vertical lines across the context bar width
      // to find the highest point that would intersect with the context bar
      const contextBarHalfWidth = (contextBarWidth.value / scale.value) / 2;
      const checkPoints = [-contextBarHalfWidth, 0, contextBarHalfWidth];
      let highestY = Infinity;

      for (const checkX of checkPoints) {
        for (let i = 0; i < 4; i++) {
          const p1 = rotatedCorners[i];
          const p2 = rotatedCorners[(i + 1) % 4];

          // Check if edge crosses the vertical line at checkX
          if ((p1.x <= checkX && p2.x >= checkX) || (p1.x >= checkX && p2.x <= checkX)) {
            // Linear interpolation to find y at x = checkX
            const t = (checkX - p1.x) / (p2.x - p1.x);
            const intersectionY = p1.y + t * (p2.y - p1.y);
            highestY = Math.min(highestY, intersectionY);
          }
        }
      }

      // Also check all corners that fall within the context bar width
      for (const corner of rotatedCorners) {
        if (corner.x >= -contextBarHalfWidth && corner.x <= contextBarHalfWidth) {
          highestY = Math.min(highestY, corner.y);
        }
      }

      // If still no intersection found, use the overall highest corner
      if (highestY === Infinity) {
        highestY = Math.min(...rotatedCorners.map(c => c.y));
      }

      // Convert from center-relative to element-top-relative coordinates
      // Original top is at y = -h/2 in center-relative coords
      // Positive values push bar away (landscape elements), negative values bring it closer (portrait elements)
      extraAbove = ((-h / 2) - highestY) * scale.value;

      // For below placement, use similar logic across the context bar width
      let lowestY = -Infinity;

      for (const checkX of checkPoints) {
        for (let i = 0; i < 4; i++) {
          const p1 = rotatedCorners[i];
          const p2 = rotatedCorners[(i + 1) % 4];

          if ((p1.x <= checkX && p2.x >= checkX) || (p1.x >= checkX && p2.x <= checkX)) {
            const t = (checkX - p1.x) / (p2.x - p1.x);
            const intersectionY = p1.y + t * (p2.y - p1.y);
            lowestY = Math.max(lowestY, intersectionY);
          }
        }
      }

      // Check corners within context bar width
      for (const corner of rotatedCorners) {
        if (corner.x >= -contextBarHalfWidth && corner.x <= contextBarHalfWidth) {
          lowestY = Math.max(lowestY, corner.y);
        }
      }

      if (lowestY === -Infinity) {
        lowestY = Math.max(...rotatedCorners.map(c => c.y));
      }

      // Original bottom is at y = h/2 in center-relative coords
      // Positive values push bar away (landscape elements), negative values bring it closer (portrait elements)
      extraBelow = (lowestY - (h / 2)) * scale.value;
    }

    const scaledTop = props.element.y * scale.value - extraAbove;
    const fitsAbove = scaledTop >= (contextBarHeight + contextBarOffset);

    return {
      top: fitsAbove
        ? -(contextBarHeight + contextBarOffset + extraAbove)
        : (props.element.height * scale.value) + contextBarOffset + extraBelow,
      fitsAbove
    };
  });

  watch(contextBarPlacement, newVal => surfaceEditorStore.setIsContextBarAbove(newVal.fitsAbove), {immediate: true});

  const contextBarStyle = computed(() => ({
    left: '50%',
    top: `${contextBarPlacement.value.top}px`,
    transform: 'translateX(-50%)'
  }));

  const elementContext = computed(() => surfaceEditorUtils.findElementInSurface({
    elementId: props.element?.id,
    surfaces: surfaces.value
  }));
  const elementSurface = computed(() => elementContext.value?.surface);
  const elementOverlaps = computed(() => {
    const element = elementContext.value?.element;
    const surface = elementSurface.value;
    if (!element || !surface) return {over: false, under: false};

    const overlaps = surfaceEditorUtils.getOverlappingElements({element, surface});
    const elementOverlapIndex = overlaps.findIndex(overlap => overlap.element.id === element.id);

    return {
      over: elementOverlapIndex < overlaps.length - 1,
      under: elementOverlapIndex > 0
    };
  });
  const isImageElement = computed(() => elementContext.value?.element?.type === SURFACE_EDITOR_ELEMENT_TYPE.image);
  const hasImageDimensions = computed(() => {
    const content = elementContext.value?.element?.content;
    return Boolean(content?.image_width && content?.image_height);
  });
  const hasSurfaceDimensions = computed(() => Boolean(elementSurface.value?.width && elementSurface.value?.height));
  const canSnapToBackground = computed(() => isImageElement.value && hasImageDimensions.value && hasSurfaceDimensions.value);

  const overflowItems = computed(() => [
    [
      {
        label: 'Copy',
        icon: 'i-ri-file-copy-2-line',
        click: () => surfaceEditorStore.copyElement({element: props.element})
      },
      {
        label: 'Paste',
        icon: 'i-ri-clipboard-line',
        disabled: !clipboard.value,
        click: () => surfaceEditorStore.pasteElement()
      },
      {
        label: 'Duplicate',
        icon: 'i-ri-file-copy-line',
        click: duplicateElement
      },
      {
        label: 'Delete',
        icon: COMMON_ICONS.delete,
        click: deleteElement
      }
    ],
    isImageElement.value [
      {
        label: 'Snap to Background',
        icon: 'i-ri-focus-3-line',
        disabled: !canSnapToBackground.value,
        click: () => {
          surfaceEditorStore.snapImageToBackground({element: props.element});
          surfaceEditorStore.reorderElements({element: props.element, action: SURFACE_EDITOR_REORDER_ACTIONS.back});
        }
      }
      ],
    [
      {
        label: 'Forward',
        icon: 'i-ri-bring-forward',
        disabled: !elementOverlaps.value.over,
        click: () => surfaceEditorStore.reorderElements({element: props.element, action: SURFACE_EDITOR_REORDER_ACTIONS.forward})
      },
      {
        label: 'Backward',
        icon: 'i-ri-send-backward',
        disabled: !elementOverlaps.value.under,
        click: () => surfaceEditorStore.reorderElements({element: props.element, action: SURFACE_EDITOR_REORDER_ACTIONS.backward})
      },
      {
        label: 'To Front',
        icon: 'i-ri-bring-to-front',
        disabled: !elementOverlaps.value.over,
        click: () => surfaceEditorStore.reorderElements({element: props.element, action: SURFACE_EDITOR_REORDER_ACTIONS.front})
      },
      {
        label: 'To Back',
        icon: 'i-ri-send-to-back',
        disabled: !elementOverlaps.value.under,
        click: () => surfaceEditorStore.reorderElements({element: props.element, action: SURFACE_EDITOR_REORDER_ACTIONS.back})
      }
    ]
  ].filter(Boolean));

  function deleteElement() {
    surfaceEditorStore.deleteElement({element: props.element});
  }

  function duplicateElement() {
    surfaceEditorStore.duplicateElement({element: props.element});
  }

</script>
